import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Download, Search, Users, Mail, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getEventAttendees, exportAttendeesCSV, getEvent } from '@/lib/api';
import { formatDate, formatPrice } from '@/lib/utils';
import { toast } from 'sonner';

export default function AttendeesPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, attendeesRes] = await Promise.all([
          getEvent(eventId),
          getEventAttendees(eventId)
        ]);
        setEvent(eventRes.data.event);
        setAttendees(attendeesRes.data.attendees || []);
      } catch (e) {
        toast.error('Failed to load attendees');
        if (e.response?.status === 401) navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, navigate]);

  const filteredAttendees = attendees.filter(a =>
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExport = async () => {
    try {
      const res = await exportAttendeesCSV(eventId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${event?.title || 'event'}_attendees.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (e) {
      toast.error('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[color:var(--pm-surface)] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading attendees...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--pm-surface)]">
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="rounded-lg">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-[color:var(--pm-ink)]">
              Attendees
            </h1>
            {event && (
              <p className="text-sm text-muted-foreground mt-1">
                {event.title} &middot; {formatDate(event.start_at)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full">
              <Users className="h-3 w-3 mr-1" />
              {attendees.length} attendees
            </Badge>
          </div>
        </div>

        <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search attendees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 rounded-lg"
                  data-testid="admin-attendees-search-input"
                />
              </div>
              <Button
                onClick={handleExport}
                variant="outline"
                className="rounded-lg"
                disabled={attendees.length === 0}
                data-testid="admin-attendees-export-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-4">
            {filteredAttendees.length > 0 ? (
              <div className="overflow-x-auto">
                <Table data-testid="admin-attendees-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4 sm:pl-6">Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAttendees.map(attendee => (
                      <TableRow key={attendee.id}>
                        <TableCell className="pl-4 sm:pl-6 font-medium text-[color:var(--pm-ink)]">
                          {attendee.name}
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            {attendee.email}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            {attendee.phone}
                          </span>
                        </TableCell>
                        <TableCell className="font-heading font-medium">
                          {formatPrice(attendee.amount_paid, attendee.currency)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(attendee.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-16">
                <Users className="h-10 w-10 mx-auto text-muted-foreground" />
                <p className="font-heading text-lg text-[color:var(--pm-ink)] mt-3">
                  {searchQuery ? 'No matching attendees' : 'No attendees yet'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchQuery ? 'Try a different search term.' : 'Attendees will appear here once tickets are purchased.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
